import React, { useState, useEffect } from "react"; // Import useState and useEffect
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useGetCartQuery,
  useUpdateCartItemMutation,
  useRemoveItemFromCartMutation,
} from "../../api/cartApi";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";

// Import View Components
import CartHeader from "./CartHeader";
import EmptyCartMessage from "./EmptyCartMessage";
import CartItemsList from "./CartItemsList";
import CartTotals from "./CartTotals";
import CouponForm from "./CouponForm";

// Define the message type
interface CouponMessage {
  type: "success" | "error";
  text: string;
}

const CartPageContainer: React.FC = () => {
  const currencySymbol = window.themeVariables?.currencySymbol || "₺";
  const { t, ready: translationsReady } = useTranslation();
  const {
    data: cart,
    isLoading,
    isError,
    error,
  } = useGetCartQuery(undefined, {
    refetchOnFocus: true,
  });
  const [updateCartItem, { isLoading: isUpdating }] =
    useUpdateCartItemMutation();
  const [removeItemFromCart, { isLoading: isRemoving }] =
    useRemoveItemFromCartMutation();

  // --- START: NEW STATE AND EFFECT FOR COUPON MESSAGES ---
  const [couponMessage, setCouponMessage] = useState<CouponMessage | null>(
    null
  );

  useEffect(() => {
    if (couponMessage) {
      const timer = setTimeout(() => setCouponMessage(null), 5000); // Hide message after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [couponMessage]);
  // --- END: NEW STATE AND EFFECT ---

  const isOverallLoading = isLoading || !translationsReady;
  const isProcessingItem = isUpdating || isRemoving;

  const handleQuantityChange = (key: string, newQuantity: number) => {
    const item = cart?.items.find((i) => i.key === key);
    if (!item) return;

    const min = item.quantityLimits.minimum;
    const max = item.quantityLimits.maximum;
    const step = item.quantityLimits.multiple_of;

    let clampedQuantity = Math.max(min, newQuantity);
    if (max > 0) {
      clampedQuantity = Math.min(max, clampedQuantity);
    }
    const adjustedQuantity = Math.floor(clampedQuantity / step) * step;
    const finalQuantity = Math.max(min, adjustedQuantity);

    if (finalQuantity !== item.quantity && finalQuantity >= min) {
      updateCartItem({ key, quantity: finalQuantity });
    }
  };

  const handleRemoveItem = (key: string) => {
    removeItemFromCart({ key });
  };

  if (isOverallLoading) {
    return (
      <div className="cart-page-container cart-page-container-loading">
        <div className="cart-header-skeleton">
          <Skeleton height={30} width={200} />
        </div>
        <div className="cart-items-skeleton">
          <div className="cart-item-header-skeleton">
            <Skeleton height={20} width="10%" />
            <Skeleton height={20} width="30%" />
            <Skeleton height={20} width="15%" />
            <Skeleton height={20} width="15%" />
            <Skeleton height={20} width="15%" />
            <Skeleton height={20} width="5%" />
          </div>
          {[...Array(3)].map((_, index) => (
            <div key={index} className="cart-item-row-skeleton">
              <Skeleton height={60} width={60} />
              <Skeleton height={20} width="80%" />
              <Skeleton height={20} width="50%" />
              <Skeleton height={36} width={100} />
              <Skeleton height={20} width="60%" />
              <Skeleton circle height={24} width={24} />
            </div>
          ))}
        </div>
        <div className="cart-totals-skeleton">
          <Skeleton height={24} width={150} style={{ marginBottom: "1rem" }} />
          <Skeleton height={20} width="80%" />
          <Skeleton height={20} width="70%" style={{ marginBottom: "1rem" }} />
          <Skeleton
            height={24}
            width="90%"
            style={{ marginBottom: "1.5rem" }}
          />
          <Skeleton height={45} width="100%" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="cart-page-container error">
        <CartHeader title={t("cart.title", "Shopping Cart")} />
        <RTKQErrorCard
          error={error as RTKQueryError | null}
          title={t("cart.errors.loadFailed", "Failed to load cart")}
        />
      </div>
    );
  }

  if (!cart || cart.itemsCount === 0) {
    return (
      <div className="cart-page-container empty">
        <CartHeader title={t("cart.title", "Shopping Cart")} />
        <EmptyCartMessage />
      </div>
    );
  }

  return (
    <div className="cart-page-container">
      <CartHeader title={t("cart.title", "Shopping Cart")} />
      <div className="cart-main-content">
        <div className="cart-items-column">
          <CartItemsList
            items={cart.items}
            isProcessing={isProcessingItem}
            onQuantityChange={handleQuantityChange}
            onRemoveItem={handleRemoveItem}
            currencySymbol={currencySymbol}
          />
          <div className="cart-actions">
            <CouponForm
              appliedCoupons={cart.coupons.map((c) => c.code)}
              onCouponResult={setCouponMessage} // --- UPDATE THIS LINE ---
              contextualClassName="coupon-actions--cart-page"
            />
            {/* --- ADD THIS BLOCK TO RENDER THE MESSAGE --- */}
            {couponMessage && (
              <div
                className={`cart-coupon-message-display ${couponMessage.type}`}
              >
                {couponMessage.text}
              </div>
            )}
            {/* --- END ADD --- */}
          </div>
        </div>

        <div className="cart-totals-column">
          <CartTotals
            totals={cart.totals}
            fees={cart.fees}
            coupons={cart.coupons}
            needsShipping={cart.needsShipping}
            hasCalculatedShipping={cart.hasCalculatedShipping}
            shippingRates={cart.shippingRates}
            currencySymbol={currencySymbol}
          />
        </div>
      </div>
    </div>
  );
};

export default CartPageContainer;
